import { FC, useMemo } from 'react';
import Drawer from '..';
import DrawerHeader from '../header';
import DrawerContent from '../content';
import { DrawerProps } from '../helpers';
import { useInfiniteQuery } from 'react-query';
import { getUserEventWithFullConversation } from '@/lib/services/user-event.service';
import { QueryKey } from '@/lib/query-client';
import Conversation from '../../conversation';
import useQueryHelper from '@/hooks/use-query-helper';
import { UserEventModel } from '@/lib/models/user-event/user-event.model';
import { cloneDeep } from 'lodash';

interface Props extends DrawerProps {
  conversationId: string;
  requestId?: string;
  anchorActivityId?: string;
}

export const ConversationDrawer: FC<Props> = ({
  conversationId,
  requestId,
  anchorActivityId,
  open,
  onClose,
  className,
  isTopDrawer,
}) => {
  const query = useInfiniteQuery(
    [QueryKey.UserEventViewWithFullConversation, conversationId, anchorActivityId],
    ({ pageParam }) =>
      getUserEventWithFullConversation(
        conversationId,
        (pageParam as string) || '',
        anchorActivityId
      ),
    {
      enabled: !!conversationId,
      getNextPageParam: ({ nextCursor }) => nextCursor,
      getPreviousPageParam: ({ previousCursor }) => previousCursor,
      cacheTime: 5 * 60 * 1000,
      staleTime: 5 * 60 * 1000,
    }
  );

  const {
    data,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    hasPreviousPage,
    isFetchingPreviousPage,
    fetchPreviousPage,
  } = query;
  const { showLoader } = useQueryHelper(query);

  const compUserEvent = useMemo(() => {
    const { pages } = data || {};

    if (!pages?.length || !pages[0]?.userEvent) {
      return new UserEventModel();
    }

    const baseUserEvent = cloneDeep(pages[0].userEvent);

    baseUserEvent.messages = {
      ...baseUserEvent.messages,
      conversation: pages.flatMap(({ userEvent }) => userEvent?.messages.conversation || []),
    };

    return baseUserEvent;
  }, [data]);

  const title = useMemo(() => {
    const { userClaim, serviceName } = compUserEvent;
    const userName = userClaim?.name || 'User';
    const serviceLabel = serviceName || 'Service';
    return `${userName}'s ${serviceLabel} Conversation`;
  }, [compUserEvent]);

  const handleLoadMoreMessages = () => {
    void fetchNextPage();
  };

  const handleLoadOlderMessages = () => {
    void fetchPreviousPage();
  };

  if (!conversationId) {
    return null;
  }

  return (
    <Drawer open={open} onClose={onClose} className={className} query={query} size="xlarge">
      <DrawerHeader onClose={onClose} isTopDrawer={isTopDrawer}>
        {title}
      </DrawerHeader>
      <DrawerContent disableScroll fullWidth>
        <Conversation
          userEvent={compUserEvent}
          variant="conversation"
          focusedRequestId={requestId}
          isLoading={showLoader}
          hasMoreMessages={hasNextPage}
          isFetchingMoreMessages={isFetchingNextPage}
          onLoadMoreMessages={handleLoadMoreMessages}
          hasOlderMessages={hasPreviousPage}
          isFetchingOlderMessages={isFetchingPreviousPage}
          onLoadOlderMessages={handleLoadOlderMessages}
        />
      </DrawerContent>
    </Drawer>
  );
};

export default ConversationDrawer;
